import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Pencil, Trash2, Clock, CalendarDays } from "lucide-react";

const DAYS = [
  { key: "senin", label: "Senin" },
  { key: "rabu", label: "Rabu" },
  { key: "jumat", label: "Jumat" },
  { key: "sabtu", label: "Sabtu" },
  { key: "minggu", label: "Minggu" },
];

function emptyForm() {
  return {
    tahun_ajaran: "", semester: "ganjil", hari: "senin",
    kelas_id: "", sesi: 1, jam_mulai: "15:30", jam_selesai: "16:30",
    mata_pelajaran_id: "", ustadz_id: "",
    is_active: true, keterangan: "",
  };
}

export default function JadwalPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kelasList, setKelasList] = useState([]);
  const [mapelList, setMapelList] = useState([]);
  const [ustadzList, setUstadzList] = useState([]);
  const [taList, setTaList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [filterTA, setFilterTA] = useState("");
  const [filterSem, setFilterSem] = useState("");
  const [filterKelas, setFilterKelas] = useState("");

  const loadRefs = async () => {
    const [k, m, u, ta] = await Promise.all([
      api.get("/kelas?limit=500"),
      api.get("/mata-pelajaran"),
      api.get("/ustadz?limit=500"),
      api.get("/tahun-ajaran"),
    ]);
    setKelasList(k.data.items || []);
    setMapelList(m.data.items || []);
    setUstadzList(u.data.items || []);
    setTaList(ta.data.items || []);
    const active = (ta.data.items || []).find(t => t.is_active);
    if (active && !filterTA) {
      setFilterTA(active.tahun_ajaran);
      setFilterSem(active.semester);
    }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filterTA) q.append("tahun_ajaran", filterTA);
      if (filterSem) q.append("semester", filterSem);
      if (filterKelas) q.append("kelas_id", filterKelas);
      const r = await api.get("/jadwal-pelajaran?" + q.toString());
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { loadItems(); }, [filterTA, filterSem, filterKelas]);

  const openCreate = () => {
    const activeTA = taList.find(t => t.is_active);
    setEditing(null);
    setForm({ ...emptyForm(), tahun_ajaran: filterTA || activeTA?.tahun_ajaran || "", semester: filterSem || activeTA?.semester || "ganjil" });
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      tahun_ajaran: it.tahun_ajaran, semester: it.semester, hari: it.hari,
      kelas_id: it.kelas_id, sesi: it.sesi, jam_mulai: it.jam_mulai, jam_selesai: it.jam_selesai,
      mata_pelajaran_id: it.mata_pelajaran_id, ustadz_id: it.ustadz_id,
      is_active: it.is_active !== false, keterangan: it.keterangan || "",
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, sesi: Number(form.sesi) };
      if (editing) {
        await api.patch(`/jadwal-pelajaran/${editing.id}`, payload);
      } else {
        await api.post("/jadwal-pelajaran", payload);
      }
      toast.success("Jadwal tersimpan");
      setDialogOpen(false);
      loadItems();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!confirm(`Hapus jadwal ${it.mata_pelajaran?.nama} — ${it.kelas?.nama} ${it.hari} sesi ${it.sesi}?`)) return;
    try { await api.delete(`/jadwal-pelajaran/${it.id}`); toast.success("Terhapus"); loadItems(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Group items into grid: day → sesi → kelas
  const byDay = useMemo(() => {
    const g = {};
    DAYS.forEach(d => { g[d.key] = []; });
    items.forEach(it => { g[it.hari]?.push(it); });
    Object.values(g).forEach(list => list.sort((a, b) => (a.sesi - b.sesi) || a.jam_mulai.localeCompare(b.jam_mulai)));
    return g;
  }, [items]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><CalendarDays className="h-6 w-6 text-emerald-600" /> Jadwal Pelajaran</h1>
          <p className="text-sm text-slate-500">Timetable mingguan: hari, sesi, kelas, mata pelajaran, ustadz pengampu.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filterTA || "all"} onValueChange={(v) => setFilterTA(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[170px]"><SelectValue placeholder="Tahun Ajaran" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua TA</SelectItem>
              {taList.map(t => <SelectItem key={t.id} value={t.tahun_ajaran}>{t.tahun_ajaran}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterSem || "all"} onValueChange={(v) => setFilterSem(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[140px]"><SelectValue placeholder="Semester" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semester</SelectItem>
              <SelectItem value="ganjil">Ganjil</SelectItem>
              <SelectItem value="genap">Genap</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterKelas || "all"} onValueChange={(v) => setFilterKelas(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[170px]"><SelectValue placeholder="Kelas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kelas</SelectItem>
              {kelasList.map(k => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="h-4 w-4 mr-1" /> Tambah</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {DAYS.map(d => (
            <Card key={d.key}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="capitalize">{d.label}</span>
                  <Badge variant="outline">{byDay[d.key]?.length || 0} sesi</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(byDay[d.key] || []).length === 0 && <p className="text-xs text-slate-400">Tidak ada jadwal.</p>}
                {(byDay[d.key] || []).map(it => (
                  <div key={it.id} className="p-3 rounded-lg border border-slate-200 bg-white hover:border-emerald-300">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                          <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-xs">Sesi {it.sesi}</span>
                          <Clock className="h-3 w-3 text-slate-400" /> {it.jam_mulai}–{it.jam_selesai}
                        </div>
                        <p className="text-sm text-slate-700 mt-1 truncate">{it.mata_pelajaran?.nama || "?"}</p>
                        <p className="text-xs text-slate-500 truncate">{it.kelas?.nama} · {it.ustadz?.nama}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(it)}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit Jadwal" : "Tambah Jadwal"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tahun Ajaran</Label>
                <Select value={form.tahun_ajaran} onValueChange={(v) => setForm(f => ({ ...f, tahun_ajaran: v }))}>
                  <SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{taList.map(t => <SelectItem key={t.id} value={t.tahun_ajaran}>{t.tahun_ajaran}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Semester</Label>
                <Select value={form.semester} onValueChange={(v) => setForm(f => ({ ...f, semester: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ganjil">Ganjil</SelectItem>
                    <SelectItem value="genap">Genap</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hari</Label>
                <Select value={form.hari} onValueChange={(v) => setForm(f => ({ ...f, hari: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DAYS.map(d => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sesi</Label>
                <Input type="number" min="1" max="20" value={form.sesi} onChange={(e) => setForm(f => ({ ...f, sesi: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Jam Mulai</Label>
                <Input type="time" value={form.jam_mulai} onChange={(e) => setForm(f => ({ ...f, jam_mulai: e.target.value }))} />
              </div>
              <div>
                <Label>Jam Selesai</Label>
                <Input type="time" value={form.jam_selesai} onChange={(e) => setForm(f => ({ ...f, jam_selesai: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Kelas</Label>
              <Select value={form.kelas_id} onValueChange={(v) => setForm(f => ({ ...f, kelas_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                <SelectContent>{kelasList.map(k => <SelectItem key={k.id} value={k.id}>{k.nama} ({k.tingkatan})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mata Pelajaran</Label>
              <Select value={form.mata_pelajaran_id} onValueChange={(v) => setForm(f => ({ ...f, mata_pelajaran_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Pilih mata pelajaran" /></SelectTrigger>
                <SelectContent>{mapelList.map(m => <SelectItem key={m.id} value={m.id}>{m.nama}{m.tingkatan ? ` (${m.tingkatan})` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ustadz Pengampu</Label>
              <Select value={form.ustadz_id} onValueChange={(v) => setForm(f => ({ ...f, ustadz_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Pilih ustadz" /></SelectTrigger>
                <SelectContent>{ustadzList.map(u => <SelectItem key={u.id} value={u.id}>{u.nama}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Keterangan</Label>
              <Input value={form.keterangan} onChange={(e) => setForm(f => ({ ...f, keterangan: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))} className="h-4 w-4" />
              Aktif
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
